if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      /** The selection as `{ name, value }` pairs, the shape of Shopify's `SelectedOption`. */
      get selectedOptions() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset, value }) => ({ name: dataset.optionName, value })
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      // Falaq editions: E1/E2 are production batches of one design. When the customer changes
      // size (or any non-edition option), pick the edition that is in stock for that choice.
      // Same price everywhere -> oldest edition first. Different prices -> keep the customer's
      // edition when it is in stock, otherwise the oldest one in stock.
      get falaqEditions() {
        const el = this.querySelector('script[data-falaq-editions]');
        if (!el) return null;
        if (this._falaqEditionsSource !== el) {
          this._falaqEditionsSource = el;
          try {
            this._falaqEditions = JSON.parse(el.textContent);
          } catch (error) {
            this._falaqEditions = null;
          }
        }
        return this._falaqEditions;
      }

      falaqResolveOptionValues(target, optionValues) {
        const editions = this.falaqEditions;
        if (!editions || !Array.isArray(editions.v)) return optionValues;

        const editionIndex = editions.pos - 1;
        const groups = Array.from(this.querySelectorAll('.product-form__input'));
        const changedIndex = groups.indexOf(target.closest('.product-form__input'));
        if (changedIndex === -1 || changedIndex === editionIndex) return optionValues;
        if (optionValues.length !== groups.length) return optionValues;

        const candidates = editions.v
          .filter(
            (variant) =>
              variant.a &&
              variant.o.every((id, index) => index === editionIndex || String(id) === String(optionValues[index]))
          )
          .sort((a, b) => a.e - b.e);
        if (!candidates.length) return optionValues;

        const samePrice = candidates.every((variant) => variant.p === candidates[0].p);
        const current = candidates.find(
          (variant) => String(variant.o[editionIndex]) === String(optionValues[editionIndex])
        );
        const pick = samePrice ? candidates[0] : current || candidates[0];

        const resolved = optionValues.slice();
        resolved[editionIndex] = String(pick.o[editionIndex]);
        return resolved;
      }

      // Falaq editions: the customer asks for more pieces than the selected edition holds. If another
      // edition of the same size and the SAME price has enough, switch to it (oldest first), so the order
      // goes through instead of failing at the cart. Different-price editions are never switched silently.
      falaqSwitchForQuantity(quantity) {
        const editions = this.falaqEditions;
        if (!editions || !Array.isArray(editions.v) || !(quantity > 1)) return false;

        const editionIndex = editions.pos - 1;
        const selected = this.selectedOptionValues.map(String);
        const current = editions.v.find((variant) => variant.o.every((id, index) => String(id) === selected[index]));
        if (!current || current.q === null || current.q === undefined || quantity <= current.q) return false;

        const twin = editions.v
          .filter(
            (variant) =>
              variant.a &&
              variant.id !== current.id &&
              variant.p === current.p &&
              (variant.q === null || variant.q === undefined || variant.q >= quantity) &&
              variant.o.every((id, index) => index === editionIndex || String(id) === selected[index])
          )
          .sort((a, b) => a.e - b.e)[0];
        if (!twin) return false;

        const input = this.querySelector(`input[data-option-value-id="${twin.o[editionIndex]}"]`);
        if (!input) return false;

        // The variant change re-renders the form and the theme puts the quantity back to its minimum:
        // keep what the customer asked for once the new edition has landed.
        const productInfo = this.closest('product-info');
        const restoreQuantity = () => {
          const quantityInput = productInfo?.querySelector('quantity-input input');
          if (quantityInput && String(quantityInput.value) !== String(quantity)) quantityInput.value = quantity;
        };
        document.addEventListener('variant:changed', () => setTimeout(restoreQuantity, 80), { once: true });
        setTimeout(restoreQuantity, 1800);

        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      connectedCallback() {
        if (this._hasChangeListener) {
          return;
        }

        this._hasChangeListener = true;

        this.addEventListener(
          'change',
          (event) => {
            // Check if target is inside this element
            const targetInThisElement = this.contains(event.target);

            // Check if there's a nested variant-selects between this and target
            let hasNestedVariantSelects = false;
            let currentElement = event.target;

            while (currentElement && currentElement !== document.body) {
              if (currentElement.tagName === 'VARIANT-SELECTS') {
                if (currentElement !== this) {
                  hasNestedVariantSelects = true;
                  break;
                } else {
                  break;
                }
              }
              currentElement = currentElement.parentElement;
            }

            // Ignore if target is not in this element or there's a nested variant-selects
            if (!targetInThisElement || hasNestedVariantSelects) {
              return;
            }

            const target = this.getInputForEventTarget(event.target);
            this.updateSelectedSwatchValue(event);
            // A combined-listing value swaps to another product, which this product's cache knows nothing
            // about: leave availability to that product's render.
            if (!target.dataset.productUrl || target.dataset.productUrl === this.dataset.url) {
              this.updateOptionAvailability();
            }
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
              data: {
                event,
                target,
                selectedOptionValues: this.falaqResolveOptionValues(event.target, this.selectedOptionValues),
              },
            });
          },
          true
        ); // Use CAPTURE phase to catch events before they bubble
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        // The "Color: Red" label next to the option name - just the clicked value's own text, so
        // unlike price/button/media this needs no variant lookup to be correct instantly. Left to the
        // section render otherwise, it would sit on the previous value for however long that fetch
        // takes.
        const selectedLabel = target.closest('.product-form__input')?.querySelector('[data-selected-swatch-value]');
        if (selectedLabel) selectedLabel.textContent = value;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            .removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }

      getVariantsCache() {
        const script = this.querySelector('[data-variants-cache]');
        if (!script) return null;

        // By content: product-info patches this picker in place, so the same script carries new JSON.
        const json = script.textContent;
        if (this._cachedVariantsSource !== json) {
          try {
            this._cachedVariants = JSON.parse(json);
          } catch (error) {
            console.error('Failed to parse the variants cache:', error);
            this._cachedVariants = null;
          }
          this._cachedVariantsSource = json;
        }

        return this._cachedVariants;
      }

      // Marks every OTHER option value available/unavailable the instant one is picked, using the same
      // cache product-info.js reads for price/button - so "Size M" stops showing available the moment
      // "Color: Red" is picked if Red only ever came in S/L, instead of waiting on the section fetch.
      //
      // Availability cascades top-down, matching Shopify's own product_option_value.available: a value
      // at position P is available when some purchasable variant carries it AND matches the currently
      // selected values of every option BEFORE P (in declared order) - options AFTER P are wildcards,
      // not fixed to their current selection. So picking a color still leaves every size available that
      // color comes in at all, even if the size currently checked isn't one of them for that color; only
      // the option that comes after size (checked as-is) narrows further.
      //
      // Two guards keep this from ever answering with a wrong or partial picture:
      // - data-complete !== 'true': past the 250 cap the cache only holds the option-value
      //   neighbourhood (see product-variant-picker.liquid), so a value missing from it might just be
      //   outside it rather than genuinely impossible - leave every value as the server rendered it.
      // - groups.length !== variants[0].options.length: this picker isn't showing every option a real
      //   variant has (e.g. a product-card's single-option picker), so position P here wouldn't line up
      //   with position P in the cached variants.
      updateOptionAvailability() {
        const variants = this.getVariantsCache();
        if (!variants?.length) return;

        const groups = Array.from(this.querySelectorAll(':scope > .product-form__input'));
        const isComplete = this.querySelector('[data-variants-cache]')?.dataset.complete === 'true';
        if (!isComplete || groups.length !== variants[0].options.length) return;

        const selected = this.selectedOptions.map(({ value }) => value);

        groups.forEach((group, groupIndex) => {
          Array.from(group.querySelectorAll('input[type="radio"], option')).forEach((valueEl) => {
            // A combined-listing value points at a different product (see product-variant-picker.liquid's
            // data-product-url) - this product's own cache has nothing to say about that one's variants.
            if (valueEl.dataset.productUrl && valueEl.dataset.productUrl !== this.dataset.url) return;

            const available = variants.some(
              (variant) =>
                variant.available &&
                variant.options.every((option, index) =>
                  index === groupIndex ? option === valueEl.value : index < groupIndex ? option === selected[index] : true
                )
            );

            this.setValueAvailability(valueEl, available);
          });
        });
      }

      setValueAvailability(valueEl, available) {
        if (valueEl.tagName === 'OPTION') {
          valueEl.textContent = available
            ? valueEl.value
            : FoxTheme.variantStrings.unavailable_with_option.replace('[value]', valueEl.value);
        } else {
          valueEl.classList.toggle('disabled', !available);
        }
      }
    }
  );
}

// Falaq editions: watch the product quantity box (typed or +/-) and let the picker switch edition.
if (!window.falaqEditionQuantityWatcher) {
  window.falaqEditionQuantityWatcher = true;
  const falaqCheckQuantity = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'quantity') return;
    const picker = input.closest('product-info')?.querySelector('variant-selects');
    if (!picker || typeof picker.falaqSwitchForQuantity !== 'function') return;
    picker.falaqSwitchForQuantity(parseInt(input.value, 10));
  };
  document.addEventListener('change', falaqCheckQuantity);
  document.addEventListener('input', falaqCheckQuantity);
}
